/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { TableUtility } from '@utility/dynamic-table';
import { toast } from 'react-toastify';
import { MODULE_NAMES } from '../../../Constants/modules';
import { AuthContext } from '../../../Contexts/AuthContext';
import FormDialog from '../../../Components/Modal/modal';
import UseAxios from '../../../Api/http/http';
import {
  getAllocationStakeholdersList,
  addAllocationStakeholder,
  editAllocationStakeholder,
  toggleAllocationStakeholderStatus,
  getVerticalDropdownOptions,
  getRolesDropdownOptions,
  getEmployeesDropdownOptions,
} from '../../../Api/request/allocationStakeholders';
import { AutoComplete } from 'primereact/autocomplete';
import ControlledSwitches from '../../../Components/Switch/switch';
import EditActionIcon from '../../../Assets/images/svg/dar_edit.svg';
import alertIcon from '../../../Assets/images/svg/alert_icon.svg';
import { allocationStakeholdersTableConfig } from './allocationStakeholdersTableConfig';
import './allocationStakeholders.scss';

const PERMISSION_ALIASES = {
  read: ['read', 'view'],
  create: ['create', 'add'],
  update: ['update', 'edit'],
};

const EMPTY_FORM = {
  verticalId: '',
  verticalName: '',
  roleId: '',
  roleName: '',
  empNum: '',
  empName: '',
  emailId: '',
};

const getApiErrorMessage = (response) => {
  if (response?.status && response.status >= 200 && response.status < 300 && response?.data?.status !== false) {
    return '';
  }
  return (
    response?.data?.response?.data?.message
    || response?.data?.response?.data?.externalMessage
    || response?.data?.message
    || (response?.data instanceof Error ? response.data.message : '')
    || ''
  );
};

const normalizeStakeholders = (response) => {
  const rows = Array.isArray(response?.data)
    ? response.data
    : response?.data?.data?.tableData
      || response?.data?.tableData
      || response?.data?.stakeholders
      || response?.data?.data
      || [];

  return rows.map((item) => normalizeStakeholder(item));
};

const normalizeStakeholder = (item) => {
  const rawEmpNum = item.resource_emp_num ?? item.resourceEmpNum ?? item.emp_no ?? item.emp_id ?? '';
  return {
    id: item.allocation_mail_id || item.id,
    allocation_mail_id: item.allocation_mail_id || item.id,
    vertical_id: item.vertical_id || item.verticalId || null,
    vertical_name: item.vertical_name || item.verticalName || '',
    resource_role_id: item.resource_role_id || item.resourceRoleId || item.role_id || item.roleId || null,
    resource_role_name: item.resource_role_name || item.resourceRoleName || item.role_name || item.roleName || '',
    resource_emp_num: rawEmpNum ? String(rawEmpNum) : '',
    resource_emp_name: item.resource_emp_name || item.resourceEmpName || '',
    resource_email_id: item.resource_email_id || item.resourceEmailId || item.email_id || item.email || '',
    isActive: Boolean(
      item.is_active === 1
      || item.is_active === true
      || item.isActive === 1
      || item.isActive === true
      || item.status === 'Active'
      || item.status === 1
      || item.status === true,
    ),
  };
};

const AllocationStakeholderForm = ({
  mode,
  form,
  fieldErrors,
  isSubmitDisabled,
  verticalOptions,
  roleOptions,
  selectedEmployee,
  employeeSuggestions,
  onSearchEmployee,
  onSelectEmployee,
  onClearEmployee,
  onChange,
  onClose,
  onSubmit,
}) => createPortal(
  <div className="allocation-stakeholders-modal-backdrop">
    <form className="allocation-stakeholders-modal" onSubmit={onSubmit}>
      <div className="allocation-stakeholders-modal__header">
        {mode === 'edit' ? 'Edit Allocation Stakeholder' : 'Add Allocation Stakeholder'}
      </div>
      <div className="allocation-stakeholders-modal__body">
        {/* Vertical Dropdown */}
        <div className="allocation-stakeholders-modal__field">
          <label htmlFor="stakeholder-vertical">
            Vertical Name<span className="required-asterisk">*</span>
          </label>
          <select
            id="stakeholder-vertical"
            name="verticalId"
            value={form.verticalId}
            onChange={onChange}
            className={`${fieldErrors.verticalId ? 'is-error' : ''} ${!form.verticalId ? 'is-placeholder' : ''}`}
          >
            <option value="" disabled>Select Vertical</option>
            {verticalOptions.map((verticalItem) => {
              const vId = verticalItem.vertical_id || verticalItem.id || verticalItem.value;
              const vName = verticalItem.vertical
                || verticalItem.vertical_name
                || verticalItem.name
                || verticalItem.label;
              return (
                <option key={vId} value={vId}>
                  {vName}
                </option>
              );
            })}
          </select>
        </div>

        {/* Role Dropdown */}
        <div className="allocation-stakeholders-modal__field">
          <label htmlFor="stakeholder-role">
            Role<span className="required-asterisk">*</span>
          </label>
          <select
            id="stakeholder-role"
            name="roleId"
            value={form.roleId}
            onChange={onChange}
            className={`${fieldErrors.roleId ? 'is-error' : ''} ${!form.roleId ? 'is-placeholder' : ''}`}
          >
            <option value="" disabled>Select Role</option>
            {roleOptions.map((roleItem) => {
              const rId = roleItem.type_id || roleItem.id || roleItem.role_id || roleItem.value;
              const rName = roleItem.resource_type || roleItem.role_name || roleItem.name || roleItem.label;
              return (
                <option key={rId} value={rId}>
                  {rName}
                </option>
              );
            })}
          </select>
        </div>

        {/* Employee Searchable AutoComplete Dropdown */}
        <div className="allocation-stakeholders-modal__field">
          <label htmlFor="stakeholder-employee">
            Employee<span className="required-asterisk">*</span>
          </label>
          <AutoComplete
            id="stakeholder-employee"
            value={selectedEmployee}
            suggestions={employeeSuggestions}
            completeMethod={onSearchEmployee}
            field="label"
            dropdown
            forceSelection
            onChange={(event) => {
              onSelectEmployee(event);
            }}
            onClear={onClearEmployee}
            placeholder="Select Employee"
            className={`allocation-stakeholder-autocomplete ${fieldErrors.empNum ? 'is-error' : ''}`}
            panelClassName="allocation-stakeholder-autocomplete-panel"
            appendTo="self"
          />
          {form.emailId && (
            <span className="field-hint">
              Email: {form.emailId}
            </span>
          )}
        </div>
      </div>
      <div className="allocation-stakeholders-modal__footer">
        <button
          type="button"
          className="allocation-stakeholders-modal__btn allocation-stakeholders-modal__btn--ghost"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="allocation-stakeholders-modal__btn allocation-stakeholders-modal__btn--primary"
          disabled={isSubmitDisabled}
        >
          {mode === 'edit' ? 'Update' : 'Add'}
        </button>
      </div>
    </form>
  </div>,
  document.body,
);

const AllocationStakeholders = () => {
  const { userProfile } = React.useContext(AuthContext);
  const permissionKey = JSON.stringify(
    userProfile?.permissions?.[MODULE_NAMES.MASTER_ALLOCATION_STAKE_HOLDERS]
      || userProfile?.permissions?.['masters:configuration:allocation-stakeholders']
      || [],
  );
  const isSuperAdmin = useMemo(() => {
    const rawLogin = String(
      userProfile?.login_type
      || userProfile?.loginType
      || userProfile?.Login_Type
      || userProfile?.user_type
      || '',
    ).trim();
    const loginType = rawLogin.toLowerCase();
    if (loginType === 'admin' || loginType === 'super admin') return true;

    const roleList = Array.isArray(userProfile?.roleDetails)
      ? userProfile.roleDetails
      : (userProfile?.roleDetails ? [userProfile.roleDetails] : []);

    return roleList.some((role) => {
      const rawRole = String(role?.roleName || role?.role_name || role?.name || '').trim();
      const name = rawRole.toLowerCase();
      const id = Number(role?.role_id ?? role?.roleId ?? role?.id);
      return name === 'super admin' || name === 'admin' || id === 1;
    });
  }, [userProfile]);

  const normalizedPermissions = useMemo(
    () => JSON.parse(permissionKey).map((action) => String(action).toLowerCase()),
    [permissionKey],
  );
  const hasAccess = useCallback((action) => {
    if (isSuperAdmin) return true;
    return PERMISSION_ALIASES[action]?.some((alias) => normalizedPermissions.includes(alias)) || false;
  }, [isSuperAdmin, normalizedPermissions]);

  const [stakeholders, setStakeholders] = useState([]);
  const [verticalOptions, setVerticalOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dialog, setDialog] = useState({ isOpen: false, mode: 'add', id: null });
  const [toggleStakeholder, setToggleStakeholder] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});

  const loadStakeholders = useCallback(async () => {
    if (!hasAccess('read')) return;
    const response = await UseAxios(getAllocationStakeholdersList(), setIsLoading);
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }
    setStakeholders(normalizeStakeholders(response));
  }, [hasAccess]);

  const loadDropdowns = useCallback(async () => {
    if (!hasAccess('read')) return;
    try {
      const [verticalsRes, rolesRes, employeesRes] = await Promise.allSettled([
        UseAxios(getVerticalDropdownOptions()),
        UseAxios(getRolesDropdownOptions()),
        UseAxios(getEmployeesDropdownOptions()),
      ]);

      if (verticalsRes.status === 'fulfilled') {
        const vData = verticalsRes.value?.data?.data
          || verticalsRes.value?.data?.verticals
          || verticalsRes.value?.data
          || [];
        setVerticalOptions(Array.isArray(vData) ? vData : []);
      }

      if (rolesRes.status === 'fulfilled') {
        const rData = rolesRes.value?.data?.data
          || rolesRes.value?.data?.roles
          || rolesRes.value?.data
          || [];
        setRoleOptions(Array.isArray(rData) ? rData : []);
      }

      if (employeesRes.status === 'fulfilled') {
        const eData = employeesRes.value?.data?.data
          || employeesRes.value?.data?.employees
          || employeesRes.value?.data
          || [];
        setEmployeeOptions(Array.isArray(eData) ? eData : []);
      }
    } catch {
      toast.error('Failed to load dropdown options.');
    }
  }, [hasAccess]);

  useEffect(() => {
    loadStakeholders();
    loadDropdowns();
  }, [loadDropdowns, loadStakeholders]);

  const closeForm = useCallback(() => {
    setDialog({ isOpen: false, mode: 'add', id: null });
    setForm(EMPTY_FORM);
    setTypedEmployeeInput('');
    setFieldErrors({});
  }, []);

  const openAdd = useCallback(() => {
    if (!hasAccess('create')) return;
    setDialog({ isOpen: true, mode: 'add', id: null });
    setForm(EMPTY_FORM);
    setTypedEmployeeInput('');
    setFieldErrors({});
  }, [hasAccess]);

  const openEdit = useCallback((item) => {
    if (!hasAccess('update')) return;

    const matchedVertical = verticalOptions.find((vertItem) => {
      const vId = vertItem.vertical_id || vertItem.id || vertItem.value;
      const vName = vertItem.vertical || vertItem.vertical_name || vertItem.name || vertItem.label;
      return String(vId) === String(item.vertical_id) || (vName && vName === item.vertical_name);
    });

    const matchedRole = roleOptions.find((roleItem) => {
      const rId = roleItem.type_id || roleItem.id || roleItem.role_id || roleItem.value;
      const rName = roleItem.resource_type || roleItem.role_name || roleItem.name || roleItem.label;
      return String(rId) === String(item.resource_role_id) || (rName && rName === item.resource_role_name);
    });

    const matchedEmployee = employeeOptions.find((emp) => {
      const empNum = emp.emp_no || emp.emp_id || emp.id || emp.value;
      const fullName = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
      const empName = emp.emp_name || fullName || emp.full_name || emp.name || emp.label;
      const email = emp.email_id || emp.email || emp.official_email || '';

      const matchByNum = item.resource_emp_num && (
        String(empNum) === String(item.resource_emp_num)
        || String(emp.emp_no) === String(item.resource_emp_num)
        || String(emp.emp_id) === String(item.resource_emp_num)
      );
      const matchByEmail = item.resource_email_id && email
        && email.toLowerCase() === item.resource_email_id.toLowerCase();
      const matchByName = item.resource_emp_name && empName
        && empName.toLowerCase() === item.resource_emp_name.toLowerCase();

      return matchByNum || matchByEmail || matchByName;
    });

    const selectedEmpNum = matchedEmployee
      ? String(matchedEmployee.emp_no || matchedEmployee.emp_id || matchedEmployee.id || '')
      : (item.resource_emp_num ? String(item.resource_emp_num) : '');
    const matchedFullName = matchedEmployee
      ? `${matchedEmployee.first_name || ''} ${matchedEmployee.last_name || ''}`.trim()
      : '';
    const selectedEmpName = matchedEmployee
      ? (matchedEmployee.emp_name || matchedFullName || matchedEmployee.full_name || '')
      : (item.resource_emp_name || '');
    const selectedEmailId = matchedEmployee
      ? (matchedEmployee.email_id || matchedEmployee.email || matchedEmployee.official_email || '')
      : (item.resource_email_id || '');

    const selectedVerticalId = matchedVertical
      ? String(matchedVertical.vertical_id || matchedVertical.id || matchedVertical.value)
      : (item.vertical_id ? String(item.vertical_id) : '');
    const selectedVerticalName = matchedVertical
      ? (matchedVertical.vertical || matchedVertical.vertical_name || matchedVertical.name || '')
      : (item.vertical_name || '');

    const selectedRoleId = matchedRole
      ? String(matchedRole.type_id || matchedRole.id || matchedRole.role_id || matchedRole.value)
      : (item.resource_role_id ? String(item.resource_role_id) : '');
    const selectedRoleName = matchedRole
      ? (matchedRole.resource_type || matchedRole.role_name || matchedRole.name || '')
      : (item.resource_role_name || '');

    setDialog({ isOpen: true, mode: 'edit', id: item.id });
    setForm({
      verticalId: selectedVerticalId,
      verticalName: selectedVerticalName,
      roleId: selectedRoleId,
      roleName: selectedRoleName,
      empNum: selectedEmpNum,
      empName: selectedEmpName,
      emailId: selectedEmailId,
    });
    setTypedEmployeeInput('');
    setFieldErrors({});
  }, [employeeOptions, hasAccess, roleOptions, verticalOptions]);

  const [employeeAutoOptions, setEmployeeAutoOptions] = useState([]);
  const [employeeSuggestions, setEmployeeSuggestions] = useState([]);

  // Transform raw employeeOptions into AutoComplete format [{ label, value, raw }]
  useEffect(() => {
    const formatted = employeeOptions.map((empItem) => {
      const empNum = String(empItem.emp_no || empItem.emp_id || empItem.id || empItem.value || '');
      const fullName = `${empItem.first_name || ''} ${empItem.last_name || ''}`.trim();
      const empName = empItem.emp_name || fullName || empItem.full_name || empItem.name || empItem.label || '';
      const email = empItem.email_id || empItem.email || empItem.official_email || '';
      const label = empName ? `${empName} (${empNum})` : empNum;
      return { label, value: empNum, empName, emailId: email, raw: empItem };
    });
    setEmployeeAutoOptions(formatted);
  }, [employeeOptions]);

  const handleSearchEmployee = useCallback((event) => {
    const query = event.query?.toLowerCase() || '';
    setEmployeeSuggestions(
      employeeAutoOptions.filter((opt) => opt.label?.toLowerCase().includes(query)),
    );
  }, [employeeAutoOptions]);

  const [typedEmployeeInput, setTypedEmployeeInput] = useState('');

  const handleSelectEmployee = useCallback((event) => {
    const val = event.value;
    if (typeof val === 'object' && val !== null) {
      setForm((prev) => ({
        ...prev,
        empNum: val.value,
        empName: val.empName,
        emailId: val.emailId,
      }));
      setTypedEmployeeInput('');
      setFieldErrors((prev) => ({ ...prev, empNum: false }));
    } else {
      setTypedEmployeeInput(val || '');
      if (!val) {
        setForm((prev) => ({
          ...prev,
          empNum: '',
          empName: '',
          emailId: '',
        }));
      }
    }
  }, []);

  const handleClearEmployee = useCallback(() => {
    setTypedEmployeeInput('');
    setForm((prev) => ({
      ...prev,
      empNum: '',
      empName: '',
      emailId: '',
    }));
  }, []);

  const selectedEmployeeObject = useMemo(() => {
    if (typedEmployeeInput !== '') {
      return typedEmployeeInput;
    }
    if (!form.empNum) return null;
    return (
      employeeAutoOptions.find((opt) => String(opt.value) === String(form.empNum)) || {
        label: form.empName ? `${form.empName} (${form.empNum})` : form.empNum,
        value: form.empNum,
        empName: form.empName,
        emailId: form.emailId,
      }
    );
  }, [employeeAutoOptions, form.emailId, form.empName, form.empNum, typedEmployeeInput]);

  const handleFormChange = useCallback((event) => {
    const { name, value } = event.target;

    if (name === 'verticalId') {
      const selectedOption = verticalOptions.find((verticalItem) => {
        const vId = verticalItem.vertical_id || verticalItem.id || verticalItem.value;
        return String(vId) === String(value);
      });
      const verticalName = selectedOption
        ? (selectedOption.vertical || selectedOption.vertical_name || selectedOption.name || selectedOption.label || '')
        : '';
      setForm((prev) => ({ ...prev, verticalId: value, verticalName }));
      setFieldErrors((prev) => ({ ...prev, verticalId: false }));
    } else if (name === 'roleId') {
      const selectedOption = roleOptions.find((roleItem) => {
        const rId = roleItem.type_id || roleItem.id || roleItem.role_id || roleItem.value;
        return String(rId) === String(value);
      });
      const roleName = selectedOption
        ? (selectedOption.resource_type || selectedOption.role_name || selectedOption.name || '')
        : '';
      setForm((prev) => ({ ...prev, roleId: value, roleName }));
      setFieldErrors((prev) => ({ ...prev, roleId: false }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  }, [roleOptions, verticalOptions]);

  const selectedStakeholder = useMemo(
    () => stakeholders.find((item) => item.id === dialog.id),
    [dialog.id, stakeholders],
  );

  const isSubmitDisabled = useMemo(() => {
    if (!form.verticalId || !form.roleId || !form.empNum) {
      return true;
    }
    if (dialog.mode === 'edit' && selectedStakeholder) {
      const isUnchanged = String(form.verticalId) === String(selectedStakeholder.vertical_id)
        && String(form.roleId) === String(selectedStakeholder.resource_role_id)
        && String(form.empNum) === String(selectedStakeholder.resource_emp_num);
      return isUnchanged;
    }
    return false;
  }, [dialog.mode, form.empNum, form.roleId, form.verticalId, selectedStakeholder]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (!hasAccess(dialog.mode === 'edit' ? 'update' : 'create')) return;

    const errors = {
      verticalId: !form.verticalId,
      roleId: !form.roleId,
      empNum: !form.empNum,
    };

    if (errors.verticalId || errors.roleId || errors.empNum) {
      setFieldErrors(errors);
      toast.error('Please fill in all required fields.');
      return;
    }

    const isDuplicate = stakeholders.some((item) => {
      if (dialog.mode === 'edit' && String(item.id) === String(dialog.id)) {
        return false;
      }
      const sameVertical = String(item.vertical_id) === String(form.verticalId);
      const sameRole = String(item.resource_role_id) === String(form.roleId);
      const sameEmpNum = String(item.resource_emp_num) === String(form.empNum);
      const sameEmail = item.resource_email_id && form.emailId
        && item.resource_email_id.toLowerCase() === form.emailId.toLowerCase();

      return sameVertical && sameRole && (sameEmpNum || sameEmail);
    });

    if (isDuplicate) {
      toast.error('An allocation stakeholder with this vertical, role, and employee already exists.');
      return;
    }

    const selectedVertical = verticalOptions.find((verticalItem) => {
      const vId = verticalItem.vertical_id || verticalItem.id || verticalItem.value;
      return String(vId) === String(form.verticalId);
    });
    const resolvedVerticalId = selectedVertical
      ? (selectedVertical.vertical_id || selectedVertical.id || selectedVertical.value)
      : (Number(form.verticalId) || selectedStakeholder?.vertical_id || null);
    const resolvedVerticalName = selectedVertical
      ? (
        selectedVertical.vertical
        || selectedVertical.vertical_name
        || selectedVertical.name
        || selectedVertical.label
        || ''
      )
      : (form.verticalName || selectedStakeholder?.vertical_name || '');

    const selectedRole = roleOptions.find((roleItem) => {
      const rId = roleItem.type_id || roleItem.id || roleItem.role_id || roleItem.value;
      return String(rId) === String(form.roleId);
    });
    const resolvedRoleId = selectedRole
      ? (selectedRole.type_id || selectedRole.id || selectedRole.role_id || selectedRole.value)
      : (Number(form.roleId) || selectedStakeholder?.resource_role_id || null);
    const resolvedRoleName = selectedRole
      ? (
        selectedRole.resource_type
        || selectedRole.role_name
        || selectedRole.name
        || selectedRole.label
        || ''
      )
      : (form.roleName || selectedStakeholder?.resource_role_name || '');

    const selectedEmployee = employeeOptions.find((empItem) => {
      const empNum = empItem.emp_no || empItem.emp_id || empItem.id || empItem.value;
      return String(empNum) === String(form.empNum);
    });
    const fullName = `${selectedEmployee?.first_name || ''} ${selectedEmployee?.last_name || ''}`.trim();
    const resolvedEmpNum = selectedEmployee
      ? (selectedEmployee.emp_no || selectedEmployee.emp_id || selectedEmployee.id || form.empNum)
      : (form.empNum || selectedStakeholder?.resource_emp_num || null);
    const resolvedEmpName = selectedEmployee
      ? (selectedEmployee.emp_name || fullName || selectedEmployee.full_name || selectedEmployee.name || '')
      : (form.empName || selectedStakeholder?.resource_emp_name || '');
    const resolvedEmailId = selectedEmployee
      ? (selectedEmployee.email_id || selectedEmployee.email || selectedEmployee.official_email || '')
      : (form.emailId || selectedStakeholder?.resource_email_id || '');

    const payload = {
      vertical_id: resolvedVerticalId ? Number(resolvedVerticalId) : null,
      vertical_name: resolvedVerticalName,
      resource_role_id: resolvedRoleId ? Number(resolvedRoleId) : null,
      resource_role_name: resolvedRoleName,
      resource_emp_num: resolvedEmpNum ? Number(resolvedEmpNum) : null,
      resource_emp_name: resolvedEmpName,
      resource_email_id: resolvedEmailId,
      is_active: dialog.mode === 'edit' ? (selectedStakeholder?.isActive ? 1 : 0) : 1,
    };

    if (dialog.mode === 'edit') {
      const response = await UseAxios(
        editAllocationStakeholder(dialog.id, payload),
        setIsLoading,
      );
      const errorMessage = getApiErrorMessage(response);
      if (errorMessage) {
        toast.error(errorMessage);
        return;
      }
      toast.success('Allocation stakeholder updated successfully.');
    } else {
      const response = await UseAxios(
        addAllocationStakeholder(payload),
        setIsLoading,
      );
      const errorMessage = getApiErrorMessage(response);
      if (errorMessage) {
        toast.error(errorMessage);
        return;
      }
      toast.success('Allocation stakeholder added successfully.');
    }

    closeForm();
    loadStakeholders();
  }, [
    closeForm,
    dialog.id,
    dialog.mode,
    employeeOptions,
    form,
    hasAccess,
    loadStakeholders,
    roleOptions,
    selectedStakeholder,
    stakeholders,
    verticalOptions,
  ]);

  const handleToggle = useCallback((item) => {
    if (!hasAccess('update')) return;
    setToggleStakeholder(item);
  }, [hasAccess]);

  const handleCloseToggleDialog = useCallback(() => {
    setToggleStakeholder(null);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!toggleStakeholder || !hasAccess('update')) return;

    const nextStatus = !toggleStakeholder.isActive;
    const response = await UseAxios(
      toggleAllocationStakeholderStatus(toggleStakeholder.id, nextStatus),
      setIsLoading,
    );
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    setStakeholders((currentStakeholders) => currentStakeholders.map((item) => (
      item.id === toggleStakeholder.id
        ? { ...item, isActive: nextStatus }
        : item
    )));
    const displayName = toggleStakeholder.resource_emp_name || 'Allocation stakeholder';
    toast.success(`${displayName} is now ${nextStatus ? 'active' : 'inactive'}.`);
    handleCloseToggleDialog();
    loadStakeholders();
  }, [handleCloseToggleDialog, hasAccess, loadStakeholders, toggleStakeholder]);

  const tableData = useMemo(() => stakeholders.map((item, index) => ({
    ...item,
    slNo: index + 1,
    vertical_name: item.vertical_name || '-',
    resource_role_name: item.resource_role_name || '-',
    resource_emp_num: item.resource_emp_num ? String(item.resource_emp_num) : '-',
    resource_emp_name: item.resource_emp_name || '-',
    status: item.isActive ? 'Active' : 'Inactive',
    action: (
      <div className="allocation-stakeholders-actions" onClick={(event) => event.stopPropagation()}>
        <ControlledSwitches
          checked={item.isActive}
          disabled={!hasAccess('update')}
          switchCss="switchCss"
          handleChange={() => handleToggle(item)}
        />
        <button
          type="button"
          className="allocation-stakeholders-action-btn"
          title="Edit Allocation Stakeholder"
          aria-label={`Edit ${item.resource_emp_name}`}
          disabled={!hasAccess('update')}
          onClick={() => openEdit(item)}
        >
          <img src={EditActionIcon} alt="" />
        </button>
      </div>
    ),
  })), [handleToggle, hasAccess, openEdit, stakeholders]);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: false,
      showPagination: false,
      showToolBar: true,
      userInfo: {},
      handlers: { handleAddIconClick: openAdd },
      toolBarSettings: {
        showAddNew: hasAccess('create'),
        showFilterOption: false,
        showDownloadExcel: false,
        showDownloadCsv: false,
        showExpandCollapse: false,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showReset: false,
        showToolBarHeading: true,
        toolBarHeading: 'Allocation Stakeholders',
      },
    },
    showToolBar: true,
    tableSettings: {
      size: 'small',
      enableTreeTable: false,
      showExpandCollapse: false,
      tableConfig: allocationStakeholdersTableConfig,
      loaderSkeleton: { rows: 8, bodyCells: 1, columns: allocationStakeholdersTableConfig.length },
      paginationConfig: { page: 1, limit: tableData.length, total: tableData.length, totalPages: 1 },
      tableScrollHeight: 'calc(100vh - 140px)',
      maxFrozenColumns: { desktop: 1, tablet: 1, mobile: 1 },
    },
  }), [hasAccess, openAdd, tableData.length]);

  if (!hasAccess('read')) {
    return (
      <div className="allocation-stakeholders-tu">
        <div>You do not have permission to view allocation stakeholders.</div>
      </div>
    );
  }

  return (
    <div className="allocation-stakeholders-tu">
      <TableUtility configuration={configuration} data={tableData} loading={isLoading} />
      {dialog.isOpen && (
        <AllocationStakeholderForm
          mode={dialog.mode}
          form={form}
          fieldErrors={fieldErrors}
          isSubmitDisabled={isSubmitDisabled}
          verticalOptions={verticalOptions}
          roleOptions={roleOptions}
          selectedEmployee={selectedEmployeeObject}
          employeeSuggestions={employeeSuggestions}
          onSearchEmployee={handleSearchEmployee}
          onSelectEmployee={handleSelectEmployee}
          onClearEmployee={handleClearEmployee}
          onChange={handleFormChange}
          onClose={closeForm}
          onSubmit={handleSubmit}
        />
      )}
      <FormDialog
        open={Boolean(toggleStakeholder)}
        title={`${toggleStakeholder?.isActive ? 'Disable' : 'Enable'} Allocation Stakeholder`}
        dialogCss="dialog-message"
        dialogPaperCss="client-enable-dialog-paper"
        paperprops={{}}
        dialogBodycontent={(
          <div className="dialog-body-text">
            <div className="dialog-alert-icon">
              <img alt="Alert Icon" className="icon-image" src={alertIcon} />
            </div>
            <div className="dialog-message-text">
              {`Are you sure you want to ${
                toggleStakeholder?.isActive ? 'disable' : 'enable'
              } this allocation stakeholder?`}
            </div>
          </div>
        )}
        button1="Cancel"
        cancelButtonId="cancel-stakeholder-toggle"
        button2={toggleStakeholder?.isActive ? 'Disable' : 'Enable'}
        submitButtonId="confirm-stakeholder-toggle"
        button1Action={handleCloseToggleDialog}
        button2Action={handleConfirmToggle}
      />
    </div>
  );
};

export default AllocationStakeholders;
